import React, { useState } from 'react';
import moment from 'moment';

/**
 * 이벤트 상세 정보 콘텐츠 컴포넌트
 * @param {object} eventData - 이벤트 데이터
 * @param {function} onDownloadCertificate - 참가증명서 다운로드 함수
 */
const EventDetailContent = ({ eventData, onDownloadCertificate }) => {
  if (!eventData) return null;

  // 시간 포맷팅 함수 (분)
  const formatTime = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`;
  };

  // 시간 포맷팅 함수 (초)
  const formatTimeFromSeconds = (seconds) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // 참가율 계산
  const participationRate = eventData.eventPeriod && eventData.participationPeriod 
    ? Math.round((eventData.participationPeriod / eventData.eventPeriod) * 100) 
    : null;

  return (
    <div className="flex flex-col">
      {/* 데스크탑 이벤트 요약 카드 (768px 이상) */}
      <div className="w-full md:max-w-[1400px] px-4 pb-6 bg-white flex-col justify-start items-start gap-6 hidden md:flex">
        <div className="self-stretch p-5 bg-neutral-50 rounded-md flex flex-col justify-center items-center gap-4">
          <div className="w-full flex-1 flex flex-col justify-center items-start gap-2 ">
            <div className="self-stretch flex flex-col justify-center items-start gap-2">
              <div className="flex justify-start items-center gap-x-2">
                {eventData.serviceTypes && (
                  <div className="size- inline-flex justify-start items-center gap-1">
                    {eventData.serviceTypes?.split(',').map((serviceType, idx) => (
                      <div key={idx}>
                        {serviceType.trim() === '라이브' && (
                          <div className="size- min-w-8 px-1 bg-white rounded outline outline-1 outline-offset-[-1px] outline-[#F43F5E] flex justify-center items-center text-[12px] leading-4 font-normal">
                            <div className="text-center justify-center text-[#F43F5E] text-[12px] font-normal leading-4">LIVE</div>
                          </div>
                        )}
                        {serviceType.trim() === '온디맨드' && (
                          <div className="size- min-w-8 px-1 bg-white rounded outline outline-1 outline-offset-[-1px] outline-indigo-500 flex justify-center items-center">
                            <div className="text-center justify-center text-indigo-500 text-xs font-normal leading-4">On Demand</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="justify-start text-zinc-500 text-sm font-normal font-['Noto_Sans_KR'] leading-none">
                  {eventData.eventStatus}
                </div>
              </div>
              <div className="self-stretch inline-flex justify-start items-center">
                <div className="flex-1 justify-center text-zinc-700 text-lg font-bold font-['Noto_Sans_CJK_KR'] leading-[24px]">{eventData.eventName}</div>
              </div>
              <div className="self-stretch justify-start text-zinc-500 text-sm font-normal font-['Noto_Sans_KR'] leading-[20px]">
                {moment(eventData.startDate).format('MM.DD HH:mm')} ~ {moment(eventData.endDate).format('MM.DD HH:mm')} KST
                {(() => {
                  try {
                    // 이벤트 기간 계산 (시간/일)
                    const startDate = moment(eventData.startDate);
                    const endDate = moment(eventData.endDate);
                    
                    // 유효한 날짜인지 확인
                    if (!startDate.isValid() || !endDate.isValid()) {
                      return '';
                    }
                    
                    const duration = moment.duration(endDate.diff(startDate));
                    const totalHours = Math.floor(duration.asHours());
                    const minutes = duration.minutes();
                    
                    // 음수 시간 방지
                    if (totalHours < 0 || (totalHours === 0 && minutes < 0)) {
                      return '';
                    }
                    
                    // 24시간 이내: HH:mm 형식, 24시간 이상: 일 단위
                    return totalHours < 24 
                      ? ` (${String(totalHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')})`
                      : ` (${Math.ceil(totalHours / 24)}일)`;
                  } catch (error) {
                    console.warn('이벤트 기간 계산 오류:', error);
                    return '';
                  }
                })()}
              </div>
            </div>
          </div>
          <div className="self-stretch inline-flex flex-col justify-start items-start gap-2">
            <div className="self-stretch h-px bg-zinc-100" />

            <div className="self-stretch h-10 inline-flex justify-end items-center gap-4">
              <div className="size- flex justify-start items-start gap-4">
                <div className="size- flex justify-start items-center gap-2">
                  <div className="justify-start text-zinc-400 text-base font-normal font-['Noto_Sans_KR'] leading-normal">총 시청 시간</div>
                  <div className="justify-center text-zinc-700 text-base font-normal font-['Noto_Sans_KR'] leading-normal">
                    {eventData.totalPlayTime == null || eventData.totalPlayTime === undefined 
                      ? '집계중' 
                      : eventData.totalPlayTime || '-'
                    }
                  </div>
                </div>
                <div data-svg-wrapper>
                  <svg width="1" height="25" viewBox="0 0 1 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="1" height="24" transform="translate(0 0.5)" fill="#F4F4F5"/>
                  </svg>
                </div>
                <div className="size- flex justify-start items-center gap-2">
                  <div className="justify-start text-zinc-400 text-base font-normal font-['Noto_Sans_KR'] leading-normal">평균 시청율</div>
                  <div className="justify-center text-zinc-700 text-base font-normal font-['Noto_Sans_KR'] leading-normal">{eventData.averageWatchRate ? `${Math.round(eventData.averageWatchRate)} %` : '-'}</div>
                </div>
                <div data-svg-wrapper>
                  <svg width="1" height="25" viewBox="0 0 1 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="1" height="24" transform="translate(0 0.5)" fill="#F4F4F5"/>
                  </svg>
                </div>
              </div>
              <div className="size- flex justify-start items-center gap-3">
                <div className="justify-start text-zinc-700 text-base font-medium font-['Noto_Sans_KR'] leading-tight">{eventData?.firstAccessTime ? '참가' : '미참가'}</div>
              </div>
             {/*  <button data-size="Default" data-status="Default" className="h-10 px-4 py-2 bg-indigo-500 rounded-md inline-flex justify-center items-center">
                  <div className="text-center justify-center text-neutral-50 text-sm font-medium font-['Noto_Sans_KR'] leading-tight">이수증</div>
                </button> */}
            </div>
          </div>
   {/*        <div className="w-px self-stretch flex justify-start items-center my-[11px]">
            <div className="flex-1 self-stretch origin-top-left bg-zinc-200" />
          </div>
          <div className="w-28 p-4 flex justify-center items-center">
            <div className={`justify-center text-lg font-medium font-['Noto_Sans_KR'] leading-tight ${
              eventData.participationStatus === '참가완료' ? 'text-indigo-500' : 'text-zinc-400'
            }`}>
              {eventData.participationStatus === '참가완료' ? '참가완료' : '미참가'}
            </div>
          </div> */}
        </div>
      </div>

      {/* 모바일 이벤트 요약 카드 (767px 이하) */}
      <div className="w-full px-4 pb-6 bg-white flex flex-col justify-start items-start gap-6 md:hidden">
        <div className="self-stretch p-4 bg-neutral-50 rounded-md inline-flex flex-col justify-center items-center gap-6">
          <div className="self-stretch flex flex-col justify-start items-start gap-2">
            {/* 서비스 타입과 상태 */}
            <div className="self-stretch inline-flex justify-start items-center">
              <div className="size- flex justify-start items-center gap-2">
                {eventData.serviceTypes && (
                  <div className="size- inline-flex justify-start items-center gap-1">
                    {eventData.serviceTypes?.split(',').map((serviceType, idx) => (
                      <div key={idx}>
                        {serviceType.trim() === '라이브' && (
                          <div className="size- min-w-8 h-4 px-1 bg-white rounded-sm outline outline-1 outline-offset-[-1px] outline-rose-500 flex justify-center items-center">
                            <div className="text-center justify-center text-rose-500 text-xs font-normal font-['Noto_Sans_KR'] leading-none">LIVE</div>
                          </div>
                        )}
                        {serviceType.trim() === '온디맨드' && (
                          <div className="size- min-w-8 h-4 px-1 bg-white rounded-sm outline outline-1 outline-offset-[-1px] outline-indigo-500 flex justify-center items-center">
                            <div className="text-center justify-center text-indigo-500 text-xs font-normal font-['Noto_Sans_KR'] leading-none">On Demand</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="justify-start text-zinc-500 text-sm font-normal font-['Noto_Sans_KR'] leading-none">
                  {eventData.eventStatus || '진행완료'}
                </div>
              </div>
            </div>
            
            {/* 이벤트명과 일정 */}
            <div className="self-stretch flex flex-col justify-center items-start gap-1">
              <div className="self-stretch justify-center text-zinc-700 text-lg font-bold font-['Noto_Sans_CJK_KR'] leading-normal">
                {eventData.eventName}
              </div>
              <div className="self-stretch inline-flex justify-start items-center gap-1">
                <div className="justify-start text-zinc-500 text-sm font-normal font-['Noto_Sans_KR'] leading-tight">
                  {moment(eventData.startDate).format('MM.DD HH:mm')} ~ {moment(eventData.endDate).format('MM.DD HH:mm')} KST
                </div>
                <div className="justify-start text-zinc-500 text-sm font-normal font-['Noto_Sans_KR'] leading-tight">
                  {/* 이벤트 기간 계산 */}
                  
          {/*         {(() => {
                    const startDate = moment(eventData.startDate);
                    const endDate = moment(eventData.endDate);
                    const duration = moment.duration(endDate.diff(startDate));
                    const hours = Math.floor(duration.asHours());
                    const minutes = duration.minutes();
                    return `(${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')})`;
                  })()} */}

                </div>
              </div>
            </div>
          </div>
          
          <div className="self-stretch flex flex-col justify-start items-start gap-2">
            {/* 참가 상태와 이수증 */}
            <div className="self-stretch inline-flex justify-between items-center">
              <div className="size- flex justify-center items-center gap-2.5">
                <div className="justify-start text-indigo-500 text-base font-medium font-['Noto_Sans_KR'] leading-tight">{eventData?.firstAccessTime ? '참가' : '미참가'}</div>
              </div>
{/*               <div className="size- flex justify-center items-center gap-1">
                <button className="h-10 px-4 py-2 bg-indigo-500 rounded-md flex justify-center items-center">
                  <div className="text-center justify-center text-neutral-50 text-sm font-medium font-['Noto_Sans_KR'] leading-tight">이수증</div>
                </button>
              </div> */}
            </div>
            
            {/* 구분선 */}
            <div className="self-stretch h-px bg-zinc-100" />
            
            {/* 상세 정보 */}
            <div className="self-stretch pb-4 flex flex-col justify-start items-start gap-4">
              <div className="self-stretch inline-flex justify-between items-center">
                <div className="justify-start text-zinc-400 text-base font-normal font-['Noto_Sans_KR'] leading-normal">총시청시간</div>
                <div className="justify-center text-zinc-500 text-base font-normal font-['Noto_Sans_KR'] leading-normal">
                  {eventData.totalPlayTime == null || eventData.totalPlayTime === undefined 
                    ? '집계중' 
                    : eventData.totalPlayTime || '-'
                  }
                </div>
              </div>
              <div className="self-stretch inline-flex justify-between items-center">
                <div className="justify-start text-zinc-400 text-base font-normal font-['Noto_Sans_KR'] leading-normal">평균시청률</div>
                <div className="justify-start text-zinc-500 text-base font-normal font-['Noto_Sans_KR'] leading-normal"> {eventData.averageWatchRate ? `${Math.round(eventData.averageWatchRate)} %` : '-'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 데스크탑 세션별 상세 테이블 (768px 이상) */}
      <div className="w-full md:max-w-[1400px] px-4 pb-4 flex-col justify-start items-start gap-4 hidden md:flex">
        <div className="self-stretch flex flex-col justify-start items-start">
          <div className="w-full self-stretch bg-white rounded-md outline outline-1 outline-offset-[-1px] outline-zinc-200 flex flex-col justify-start items-start overflow-hidden">
            {/* 테이블 헤더 */}
            <div className="self-stretch h-12 px-5 rounded-tl-lg border-b border-solid border-zinc-200 inline-flex justify-start items-center">
              <div className="flex-1 h-12 px-4 flex justify-start items-center">
                <div className="flex-1 text-center justify-center text-zinc-400 text-xs font-normal font-['Noto_Sans_KR']">세션</div>
              </div>
              <div className="w-28 h-12 px-4 flex justify-center items-center">
                <div className="flex-1 text-center justify-center text-zinc-400 text-xs font-normal font-['Noto_Sans_KR']">최초참가시간</div>
              </div>
              <div className="w-24 h-12 px-4 flex justify-center items-center">
                <div className="flex-1 text-center justify-center text-zinc-400 text-xs font-normal font-['Noto_Sans_KR']">시청시간</div>
              </div>
              <div className="w-20 h-12 px-4 flex justify-center items-center">
                <div className="flex-1 text-center justify-center text-zinc-400 text-xs font-normal font-['Noto_Sans_KR']">시청률</div>
              </div>
              <div className="w-24 h-12 px-4 flex justify-center items-center">
                <div className="flex-1 text-center justify-center text-zinc-400 text-xs font-normal font-['Noto_Sans_KR']">상태</div>
              </div>
             {/*  <div className="w-20 h-12 px-4 flex justify-center items-center">
                <button className="flex-1 text-center justify-center text-zinc-400 text-xs font-normal font-['Noto_Sans_KR']">이수증</button>
              </div> */}
            </div>

            {/* 테이블 콘텐츠 */}
            <div className="self-stretch flex flex-col justify-center items-start">
              {eventData.sessions && eventData.sessions.length > 0 ? (
                eventData.sessions.map((session, index) => (
                  <div key={session.sessionIdx || index} className="self-stretch px-5 py-4 border-b border-zinc-200 border-solid inline-flex justify-start items-center">
                    {/* 세션 정보 */}
                    <div className="flex-1 inline-flex flex-col justify-center items-start gap-2">
                      {/* 서비스 타입 뱃지 */}
                      <div className="flex justify-start items-center gap-x-2">  
                        <div className="self-stretch inline-flex justify-start items-center gap-1">
                          {session.serviceType === 'LIVE' && (
                            <div className="size- min-w-8 px-1 bg-white rounded outline outline-1 outline-offset-[-1px] outline-[#F43F5E] flex justify-center items-center text-[12px] leading-4 font-normal">
                              <div className="text-center justify-center text-[#F43F5E] text-[12px] font-normal leading-4">LIVE</div>
                            </div>
                          )}
                          {session.serviceType === 'ONDEMAND' && (
                            <div className="size- min-w-8 px-1 bg-white rounded outline outline-1 outline-offset-[-1px] outline-indigo-500 flex justify-center items-center">
                              <div className="text-center justify-center text-indigo-500 text-xs font-normal leading-4">On Demand</div>
                            </div>
                          )}
                        </div>
                        <div className={`justify-start text-zinc-500 text-sm font-normal font-['Noto_Sans_KR'] leading-none ${
                          session.sessionStatus === '진행중' ? 'text-indigo-500' : 
                          session.sessionStatus === '진행완료' ? 'text-zinc-500' : 
                          'text-zinc-400'
                        }`}>
                          {session.sessionStatus || '-'}
                        </div>
                      </div>
                      {/* 세션명 */}
                      <div className="self-stretch inline-flex justify-start items-center">
                        <div className="flex-1 justify-center text-zinc-700 text-lg font-normal font-['Noto_Sans_CJK_KR'] leading-[24px]">
                          {session.sessionName || '세션명 없음'}
                        </div>
                      </div>
                      <div className="self-stretch justify-start text-zinc-500 text-sm font-normal font-['Noto_Sans_KR'] leading-[20px]">
                        {session.sessionStartDate && session.sessionEndDate ? (
                          (() => {
                            const sessionStartDate = moment(session.sessionStartDate);
                            const sessionEndDate = moment(session.sessionEndDate);
                            const duration = moment.duration(sessionEndDate.diff(sessionStartDate));
                            const totalHours = Math.floor(duration.asHours());
                            const minutes = duration.minutes();
                            
                            // 24시간 이내: HH:mm 형식, 24시간 이상: 일 단위
                            const durationText = totalHours < 24 
                              ? `(${String(totalHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')})`
                              : `(${Math.ceil(totalHours / 24)}일)`;
                            
                            return `${sessionStartDate.format('MM.DD HH:mm')} ~ ${sessionEndDate.format('MM.DD HH:mm')} KST ${durationText}`;
                          })()
                        ) : '-'}
                      </div>
                    </div>

                    

                    {/* 최초 참가 시간 */}
                    <div className="w-28 flex justify-center items-center">
                      <div className="justify-center text-zinc-500 text-base font-normal font-['Noto_Sans_KR'] leading-normal">
                        {session.firstAccessTime ? 
                          moment(session.firstAccessTime).format('MM.DD HH:mm') : 
                          (session.isParticipated === 'Y' ? '참가기록 있음' : '-')
                        }
                      </div>
                    </div>

                    {/* 진행시간 */}
                    {/* <div className="w-28 p-4 flex justify-center items-center">
                      <div className="justify-center text-zinc-500 text-base font-normal font-['Noto_Sans_KR'] leading-normal">
                        {session.sessionDurationFormatted || '-'}
                      </div>
                    </div> */}

                    {/* 시청 시간 */}
                    <div className="w-24 p-4 flex justify-center items-center">
                      <div className="justify-center text-zinc-500 text-base font-normal font-['Noto_Sans_KR'] leading-normal">
                        {session.isParticipated === 'Y' ? 
                          formatTimeFromSeconds(session.watchTimeSeconds) : 
                          '-'
                        }
                      </div>
                    </div>

                    {/* 시청률 */}
                    <div className="w-20 p-4 flex justify-center items-center">
                      <div className="justify-center text-zinc-500 text-base font-normal font-['Noto_Sans_KR'] leading-normal">
                        {session.isParticipated === 'Y' ? 
                          `${Math.round(session.watchRate || 0)}%` : 
                          '-'
                        }
                      </div>
                    </div>

                    {/* 상태 */}
                    <div className="w-24 p-4 flex justify-center items-center">
                      <div className="justify-center text-zinc-500 text-base font-normal font-['Noto_Sans_KR'] leading-normal">
                        {session?.firstAccessTime ? '참가' : '미참가'}
                      </div>
                    </div>

                    {/* 이수증 */}
            {/*         <div className="w-20 flex justify-center items-center">
                      <button data-filled="Default" data-size="Default" className="h-10 px-4 py-2 bg-white rounded-md outline outline-1 outline-offset-[-1px] outline-zinc-200 inline-flex justify-center items-center">
                        <div className="text-center justify-center text-zinc-500 text-sm font-medium font-['Noto_Sans_KR'] leading-tight">이수증</div>
                      </button>
                    </div> */}
                  </div>
                ))
              ) : (
                <div className="self-stretch px-5 py-8 flex justify-center items-center">
                  <div className="text-zinc-400 text-base font-normal font-['Noto_Sans_KR']">
                    세션 정보가 없습니다.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 모바일 세션별 카드 리스트 (767px 이하) */}
      <MobileSessionList 
        sessions={eventData.sessions} 
        formatTimeFromSeconds={formatTimeFromSeconds}
      />

      {/* 참가증명서 다운로드 버튼 (하단) */}
      {/* {eventData.participationStatus === '참가완료' && (
        <div className="w-full max-w-[1400px] px-4 pb-4 flex justify-center">
          <button 
            className="px-6 py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors text-base font-medium"
            onClick={() => onDownloadCertificate && onDownloadCertificate(eventData)}
          >
            참가증명서 다운로드
          </button>
        </div>
      )} */}
    </div>
  );
};

// 모바일 세션 리스트 컴포넌트 (토글 기능 포함)
const MobileSessionList = ({ sessions, formatTimeFromSeconds }) => {
  // 각 세션의 펼침/접힘 상태 관리
  const [expandedSessions, setExpandedSessions] = useState({});

  // 세션 토글 함수
  const toggleSession = (sessionId) => {
    setExpandedSessions(prev => ({
      ...prev,
      [sessionId]: !prev[sessionId]
    }));
  };

  if (!sessions || sessions.length === 0) {
    return (
      <div className="w-full px-4 pb-4 flex flex-col justify-start items-start gap-4 md:hidden">
        <div className="self-stretch p-4 flex justify-center items-center">
          <div className="text-zinc-400 text-base font-normal font-['Noto_Sans_KR']">
            세션 정보가 없습니다.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 pb-4 flex flex-col justify-start items-start gap-4 md:hidden">
      <div className="self-stretch inline-flex flex-col justify-center items-start gap-4">
        {sessions.map((session, index) => {
          
          const sessionId = session.sessionIdx || index;
          const isExpanded = expandedSessions[sessionId];
          
          // 세션 기간 계산 (시간/일)
          const sessionStartDate = moment(session.sessionStartDate);
          const sessionEndDate = moment(session.sessionEndDate);
          const duration = moment.duration(sessionEndDate.diff(sessionStartDate));
          const totalHours = Math.floor(duration.asHours());
          const minutes = duration.minutes();
          
          // 24시간 이내: HH:mm 형식, 24시간 이상: 일 단위
          const durationText = totalHours < 24 
            ? `(${String(totalHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')})`
            : `(${Math.ceil(totalHours / 24)}일)`;

          return (
            <div key={sessionId} className="self-stretch p-4 rounded-lg outline outline-1 outline-offset-[-1px] outline-zinc-200 inline-flex flex-col justify-center items-start">
              {/* 상단 정보 영역 */}
              <div className="self-stretch flex flex-col justify-start items-start gap-6">
                <div className="self-stretch flex flex-col justify-start items-start gap-1">
                  {/* 서비스 타입과 상태 */}
                  <div className="self-stretch inline-flex justify-start items-center">
                    <div className="size- flex justify-start items-center gap-2">
                      {session.serviceType === 'LIVE' && (
                        <div className="size- min-w-8 h-4 px-1 bg-white rounded-sm outline outline-1 outline-offset-[-1px] outline-rose-500 flex justify-center items-center">
                          <div className="text-center justify-center text-rose-500 text-xs font-normal font-['Noto_Sans_KR'] leading-none">LIVE</div>
                        </div>
                      )}
                      {session.serviceType === 'ONDEMAND' && (
                        <div className="size- min-w-8 h-4 px-1 bg-white rounded-sm outline outline-1 outline-offset-[-1px] outline-indigo-500 flex justify-center items-center">
                          <div className="text-center justify-center text-indigo-500 text-xs font-normal font-['Noto_Sans_KR'] leading-none">On Demand</div>
                        </div>
                      )}
                      <div className="justify-start text-zinc-500 text-sm font-normal font-['Noto_Sans_KR'] leading-none">
                        {session.sessionStatus || '진행완료'}
                      </div>
                    </div>
                  </div>
                  
                  {/* 세션명과 일정 */}
                  <div className="self-stretch inline-flex justify-start items-start gap-2.5">
                    <div className="flex-1 pt-2 inline-flex flex-col justify-start items-start gap-1">
                      <div className="w-72 justify-center text-zinc-700 text-base font-normal font-['Noto_Sans_KR'] leading-normal">
                        {session.sessionName || '세션명 없음'}
                      </div>
                      <div className="self-stretch inline-flex justify-start items-center gap-1">
                        <div className="justify-start text-zinc-500 text-sm font-normal font-['Noto_Sans_KR'] leading-tight">
                          {session.sessionStartDate && session.sessionEndDate ? (
                            `${moment(session.sessionStartDate).format('MM.DD HH:mm')} ~ ${moment(session.sessionEndDate).format('MM.DD HH:mm')} KST`
                          ) : '-'}
                        </div>
                        <div className="justify-start text-zinc-500 text-sm font-normal font-['Noto_Sans_KR'] leading-tight">
                          {durationText}
                        </div>
                      </div>
                    </div>
                    
                    {/* 토글 화살표 아이콘 */}
                    <div 
                      className="size-10 px-4 py-2 rounded-full flex justify-center items-center cursor-pointer hover:bg-zinc-100 transition-colors"
                      onClick={() => toggleSession(sessionId)}
                    >
                      <div className="relative">
                        <svg 
                          width="24" 
                          height="24" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                          className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        >
                          <path fillRule="evenodd" clipRule="evenodd" d="M5.29289 8.29289C5.68342 7.90237 6.31658 7.90237 6.70711 8.29289L12 13.5858L17.2929 8.29289C17.6834 7.90237 18.3166 7.90237 18.7071 8.29289C19.0976 8.68342 19.0976 9.31658 18.7071 9.70711L12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L5.29289 9.70711C4.90237 9.31658 4.90237 8.68342 5.29289 8.29289Z" fill="#3F3F46"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* 기본 정보 (항상 표시) */}
                <div className="self-stretch inline-flex justify-between items-center">
                  <div className="size- flex justify-center items-center gap-2.5">
                    <div className="justify-start text-zinc-400 text-base font-normal font-['Noto_Sans_KR'] leading-normal">
                      {session?.firstAccessTime ? '참가' : '미참가'}
                    </div>
                  </div>
           {/*        <div className="size- flex justify-center items-center gap-1">
                    <button className="h-10 px-4 py-2 bg-white rounded-md outline outline-1 outline-offset-[-1px] outline-zinc-200 flex justify-center items-center">
                      <div className="text-center justify-center text-zinc-500 text-sm font-medium font-['Noto_Sans_KR'] leading-tight">이수증</div>
                    </button>
                  </div> */}
                </div>
              </div>

              {/* 상세 정보 (펼쳐졌을 때만 표시) - 애니메이션 효과 */}
              <div className={`w-full overflow-hidden transition-all duration-300 ease-in-out ${
                isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="self-stretch flex flex-col justify-start items-start gap-2 pt-2">
                  {/* 구분선 */}
                  <div className="self-stretch h-px bg-zinc-100" />
                  
                  {/* 상세 정보 */}
                  <div className="self-stretch flex flex-col justify-start items-start gap-4">
                    <div className="self-stretch inline-flex justify-between items-center">
                      <div className="justify-start text-zinc-400 text-base font-normal font-['Noto_Sans_KR'] leading-normal">최초 참가 시간</div>
                      <div className="justify-center text-zinc-500 text-base font-normal font-['Noto_Sans_KR'] leading-normal">
                        {session.firstAccessTime ? 
                          moment(session.firstAccessTime).format('MM.DD HH:mm') : 
                          (session.isParticipated === 'Y' ? '참가기록 있음' : '-')
                        }
                      </div>
                    </div>
                    <div className="self-stretch inline-flex justify-between items-center">
                      <div className="justify-start text-zinc-400 text-base font-normal font-['Noto_Sans_KR'] leading-normal">시청시간</div>
                      <div className="justify-start text-zinc-500 text-base font-normal font-['Noto_Sans_KR'] leading-normal">
                        {session.isParticipated === 'Y' ? 
                          formatTimeFromSeconds(session.watchTimeSeconds) : 
                          '-'
                        }
                      </div>
                    </div>
                    <div className="self-stretch inline-flex justify-between items-center">
                      <div className="justify-start text-zinc-400 text-base font-normal font-['Noto_Sans_KR'] leading-normal">시청율</div>
                      <div className="justify-start text-zinc-500 text-base font-normal font-['Noto_Sans_KR'] leading-normal">
                        {session.isParticipated === 'Y' ? 
                          `${Math.round(session.watchRate || 0)}%` : 
                          '-'
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EventDetailContent;
